import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const eventToken = searchParams.get('token');
  const eventId = searchParams.get('event_id');

  const [event, setEvent] = useState(null);
  const [eventError, setEventError] = useState('');

  const [form, setForm] = useState({ full_name: '', mobile_number: '' });
  const [errors, setErrors] = useState({});
  const [selfieBlob, setSelfieBlob] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);

  // Returning visitor state
  const [returningVisitor, setReturningVisitor] = useState(null); // { name, selfie_url }
  const [alreadyRegistered, setAlreadyRegistered] = useState(null); // null | { name, gallery_url }
  const [mobileChecking, setMobileChecking] = useState(false);
  const [useExistingSelfie, setUseExistingSelfie] = useState(false);
  const [updatingSelfie, setUpdatingSelfie] = useState(false); // already-registered visitor replacing their selfie

  const [cameraActive, setCameraActive] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [facingMode, setFacingMode] = useState('user'); // 'user' = front, 'environment' = back
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load event info — supports both ?token=UUID and ?event_id=N
  useEffect(() => {
    if (!eventId && !eventToken) {
      setEventError('Invalid QR code. No event or token found.');
      return;
    }
    const url = eventToken
      ? `${API_URL}/api/register/event/by-token?token=${eventToken}`
      : `${API_URL}/api/register/event/${eventId}`;
    axios.get(url)
      .then(res => setEvent(res.data))
      .catch(() => setEventError('Event not found. Please scan the QR code again.'));
  }, [eventId, eventToken]);

  // Check returning visitor when mobile is fully entered
  const checkMobile = useCallback(async (mobile) => {
    if (!/^\d{10}$/.test(mobile)) return;
    setMobileChecking(true);
    setReturningVisitor(null);
    setAlreadyRegistered(null);
    setUseExistingSelfie(false);
    try {
      const params = eventToken ? `?token=${eventToken}` : `?event_id=${eventId}`;
      const res = await axios.get(`${API_URL}/api/register/check-mobile/${mobile}${params}`);
      if (res.data.exists) {
        setReturningVisitor(res.data);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setAlreadyRegistered({
          name: err.response.data?.name || '',
          gallery_url: err.response.data?.gallery_url || null
        });
      }
    } finally {
      setMobileChecking(false);
    }
  }, [eventId]);

  // Attach stream AFTER <video> is in the DOM.
  // The old bug: startCamera() assigned srcObject then called setCameraActive(true).
  // Because cameraActive controls whether <video> renders, videoRef.current was
  // null at assignment time — the stream was lost and the video never played.
  // Fix: setCameraActive(true) first (mounts the element), then this effect
  // assigns srcObject once the ref is valid, and waits for 'playing' + a
  // 200ms polling fallback before enabling the Capture button.
  useEffect(() => {
    if (!cameraActive || !streamRef.current) return;

    const video = videoRef.current;
    if (!video) return;

    video.srcObject = streamRef.current;

    const onPlaying = () => setVideoReady(true);
    video.addEventListener('playing', onPlaying);

    // Fallback for browsers/devices where 'playing' fires unreliably
    const timer = setInterval(() => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setVideoReady(true);
        clearInterval(timer);
      }
    }, 200);

    return () => {
      video.removeEventListener('playing', onPlaying);
      clearInterval(timer);
    };
  }, [cameraActive]);

  // Start camera — acquire stream, then flip flag to mount <video>
  const startCamera = useCallback(async (facing) => {
    setCameraError('');
    setVideoReady(false);
    const mode = facing || 'user';
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 720 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      setFacingMode(mode);
      setCameraActive(true); // <video> mounts → useEffect above runs
    } catch (err) {
      setCameraError('Camera access denied. Please allow camera access and try again.');
    }
  }, []);

  // Flip between front and back camera
  const flipCamera = useCallback(async () => {
    const next = facingMode === 'user' ? 'environment' : 'user';
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setVideoReady(false);
    setCameraActive(false);
    // Small delay so React unmounts <video> before remounting with new stream
    setTimeout(() => startCamera(next), 100);
  }, [facingMode, startCamera]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setVideoReady(false);
    setCameraActive(false);
  }, []);

  // Capture selfie
  const captureSelfie = useCallback(() => {
    if (!videoRef.current) return;
    const { videoWidth, videoHeight } = videoRef.current;
    if (!videoWidth || !videoHeight) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    const ctx = canvas.getContext('2d');
    // Mirror only for front camera
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(videoRef.current, 0, 0);
    canvas.toBlob(blob => {
      setSelfieBlob(blob);
      setSelfiePreview(URL.createObjectURL(blob));
      stopCamera();
    }, 'image/jpeg', 0.9);
  }, [stopCamera, facingMode]);

  // Retake
  const retakeSelfie = () => {
    setSelfieBlob(null);
    setSelfiePreview(null);
    startCamera();
  };

  // Upload from device gallery
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUpdatingSelfie(true);
    setSelfieBlob(file);
    setSelfiePreview(URL.createObjectURL(file));
    stopCamera();
    e.target.value = '';
  };

  // Submit updated selfie for already-registered visitor
  const handleUpdateSelfie = async () => {
    if (!selfieBlob) { setErrors({ selfie: 'Please capture or upload a selfie first.' }); return; }
    setErrors({});
    setSubmitting(true);
    try {
      const formData = new FormData();
      if (eventToken) formData.append('token', eventToken);
      else formData.append('event_id', eventId);
      formData.append('mobile_number', form.mobile_number);
      formData.append('selfie', selfieBlob, 'selfie.jpg');
      const res = await axios.post(`${API_URL}/api/register/update-selfie`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Update failed. Please try again.';
      setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => () => stopCamera(), [stopCamera]);

  const validateForm = () => {
    const errs = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2)
      errs.full_name = 'Please enter your full name.';
    if (!/^\d{10}$/.test(form.mobile_number))
      errs.mobile_number = 'Enter a valid 10-digit mobile number.';
    if (!useExistingSelfie && !selfieBlob)
      errs.selfie = 'Please capture your selfie.';
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);
    try {
      let res;
      if (useExistingSelfie) {
        // Returning visitor reusing stored selfie
        res = await axios.post(`${API_URL}/api/register/reuse-selfie`, {
          ...(eventToken ? { token: eventToken } : { event_id: eventId }),
          mobile_number: form.mobile_number
        });
      } else {
        // New selfie registration
        const formData = new FormData();
        if (eventToken) formData.append('token', eventToken);
        else formData.append('event_id', eventId);
        formData.append('full_name',     form.full_name.trim());
        formData.append('mobile_number', form.mobile_number);
        formData.append('selfie',        selfieBlob, 'selfie.jpg');
        res = await axios.post(`${API_URL}/api/register`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setSuccess(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Registration failed. Please try again.';
      setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const isFormComplete = form.full_name.trim().length >= 2 &&
    /^\d{10}$/.test(form.mobile_number) &&
    (useExistingSelfie || !!selfieBlob);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (eventError) return <ErrorScreen message={eventError} />;
  if (!event) return <LoadingScreen />;
  if (success) return <ProcessingScreen data={success} event={event} />;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Background image with fade-from-middle effect */}
        <div style={styles.cardBackgroundImage}></div>

        {/* Content wrapper to stay above the background */}
        <div style={styles.contentWrapper}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.logo}>📸</div>
            <h1 style={styles.title}>Event Photo Access</h1>
            <p style={styles.eventName}>{event.name}</p>
            <p style={styles.eventMeta}>
              {new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              {event.venue && ` · ${event.venue}`}
            </p>
          </div>

          <div style={styles.divider} />

          <p style={styles.instructions}>
            Register below to receive a personalized album of photos where you appear.
            Your selfie will be used to identify you in event photos.
          </p>

          {/* Name */}
          <div style={styles.field}>
            <label style={styles.label}>Full Name</label>
            <input
              style={{ ...styles.input, ...(errors.full_name ? styles.inputError : {}) }}
              placeholder="Enter your full name"
              value={form.full_name}
              onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
            />
            {errors.full_name && <p style={styles.error}>{errors.full_name}</p>}
          </div>

          {/* Mobile */}
          <div style={styles.field}>
            <label style={styles.label}>Mobile Number</label>
            <input
              style={{ ...styles.input, ...(errors.mobile_number ? styles.inputError : {}) }}
              placeholder="10-digit mobile number"
              value={form.mobile_number}
              onChange={e => {
                setAlreadyRegistered(false);
                setReturningVisitor(null);
                setUseExistingSelfie(false);
                setMobileChecking(false);
                setForm(f => ({ ...f, mobile_number: e.target.value.replace(/\D/g, '').slice(0, 10) }));
              }}
              onBlur={() => checkMobile(form.mobile_number)}
              maxLength={10}
              inputMode="numeric"
            />
            {mobileChecking && <p style={styles.mobileChecking}>Checking…</p>}
            {errors.mobile_number && <p style={styles.error}>{errors.mobile_number}</p>}
          </div>

          {/* Already registered for this event */}
          {alreadyRegistered && !updatingSelfie && (
            <div style={styles.alreadyBox}>
              <div style={styles.alreadyIcon}>✅</div>
              <div style={{ width: '100%' }}>
                <p style={styles.alreadyTitle}>
                  {alreadyRegistered.name ? `Welcome back, ${alreadyRegistered.name}!` : "You're already registered!"}
                </p>
                <p style={styles.alreadySubtitle}>You are already registered for this event.</p>
                {alreadyRegistered.gallery_url && (
                  <a
                    href={alreadyRegistered.gallery_url}
                    style={{ display: 'inline-block', marginTop: '0.75rem', padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #a78bfa, #60a5fa)', color: '#fff', borderRadius: '0.6rem', fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem' }}
                  >
                    View Your Photos →
                  </a>
                )}
                <p style={{ margin: '1rem 0 0.5rem', fontSize: '0.8rem', color: '#666' }}>
                  Photos not matching? Update your selfie:
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />
                  <button
                    style={{ ...styles.reuseBtn, fontSize: '0.82rem', padding: '0.45rem 0.9rem' }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Upload Selfie
                  </button>
                  <button
                    style={{ ...styles.reuseBtn, fontSize: '0.82rem', padding: '0.45rem 0.9rem' }}
                    onClick={() => { setUpdatingSelfie(true); startCamera(); }}
                  >
                    Capture New Selfie
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Selfie update mode — shown after visitor chooses to re-capture */}
          {alreadyRegistered && updatingSelfie && (
            <div style={{ ...styles.alreadyBox, flexDirection: 'column', alignItems: 'flex-start' }}>
              <p style={{ ...styles.alreadyTitle, marginBottom: '0.25rem' }}>Update your selfie</p>
              <p style={{ ...styles.alreadySubtitle, marginBottom: '0.75rem' }}>
                Take a clear, well-lit, front-facing photo for best results.
              </p>
              <button style={{ ...styles.reuseBtn, fontSize: '0.82rem', marginBottom: '0.5rem' }}
                onClick={() => { setUpdatingSelfie(false); setSelfieBlob(null); setSelfiePreview(null); stopCamera(); }}>
                ← Cancel
              </button>
            </div>
          )}

          {/* Returning visitor — offer stored selfie */}
          {returningVisitor && (
            <div style={styles.returningBox}>
              <p style={styles.returningTitle}>Welcome back, {returningVisitor.name}!</p>
              <p style={styles.returningSubtitle}>We found your previous registration. Use your stored selfie or take a new one.</p>
              <div style={styles.returningSelfiRow}>
                <img
                  src={`${API_URL}${returningVisitor.selfie_url}`}
                  alt="Stored selfie"
                  style={styles.returningThumb}
                />
                <div style={styles.returningButtons}>
                  <button
                    style={{ ...styles.reuseBtn, ...(useExistingSelfie ? styles.reuseBtnActive : {}) }}
                    onClick={() => { setUseExistingSelfie(true); setSelfieBlob(null); setSelfiePreview(null); stopCamera(); }}
                  >
                    ✓ Use this selfie
                  </button>
                  <button
                    style={{ ...styles.reuseBtn, ...(!useExistingSelfie ? styles.reuseBtnActive : {}) }}
                    onClick={() => { setUseExistingSelfie(false); }}
                  >
                    📷 Take new selfie
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Selfie — hidden when using existing, or already registered and not in update mode */}
          <div style={{ ...styles.field, ...((useExistingSelfie || (!!alreadyRegistered && !updatingSelfie)) ? { display: 'none' } : {}) }}>
            <label style={styles.label}>Selfie</label>
            {!cameraActive && !selfiePreview && (
              <button style={styles.cameraBtn} onClick={startCamera}>
                📷 Open Camera
              </button>
            )}
            {cameraError && <p style={styles.error}>{cameraError}</p>}

            {cameraActive && (
              <div style={styles.cameraContainer}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ ...styles.video, transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                />
                {!videoReady && (
                  <p style={styles.cameraLoading}>Starting camera…</p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                  <button
                    style={{
                      ...styles.captureBtn,
                      ...(!videoReady ? styles.captureBtnDisabled : {})
                    }}
                    onClick={captureSelfie}
                    disabled={!videoReady}
                  >
                    ● Capture
                  </button>
                  <button
                    style={{
                      ...styles.captureBtn,
                      background: '#374151',
                      padding: '0.6rem 1rem',
                      fontSize: '1.1rem',
                      minWidth: 'unset'
                    }}
                    onClick={flipCamera}
                    title={facingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}
                  >
                    🔄
                  </button>
                </div>
              </div>
            )}

            {selfiePreview && (
              <div style={styles.previewContainer}>
                <img src={selfiePreview} alt="Selfie preview" style={styles.preview} />
                <button style={styles.retakeBtn} onClick={retakeSelfie}>
                  ↺ Retake
                </button>
              </div>
            )}
            {errors.selfie && <p style={styles.error}>{errors.selfie}</p>}
          </div>

          {errors.submit && (
            <div style={styles.submitError}>{errors.submit}</div>
          )}

          {(!alreadyRegistered || updatingSelfie) && (
            <button
              style={{ ...styles.registerBtn, ...((!isFormComplete || submitting) ? styles.registerBtnDisabled : {}) }}
              onClick={updatingSelfie ? handleUpdateSelfie : handleSubmit}
              disabled={(updatingSelfie ? !selfieBlob : !isFormComplete) || submitting}
            >
              {submitting ? 'Processing...' : updatingSelfie ? 'Update Selfie' : 'Register'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────

const ProcessingScreen = ({ data, event }) => {
  const [countdown, setCountdown] = useState(12);
  const [redirected, setRedirected] = useState(false);

  useEffect(() => {
    if (!data.gallery_url) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setRedirected(true);
          window.location.href = data.gallery_url;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [data.gallery_url]);

  return (
    <div style={{ ...styles.page, background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)' }}>
      <div style={{ ...styles.card, textAlign: 'center', padding: '3rem 2rem' }}>
        {data.gallery_url ? (
          <>
            {/* Spinner */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{
                width: 72, height: 72, margin: '0 auto',
                border: '6px solid #e0e7ff',
                borderTop: '6px solid #4f46e5',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
            <h2 style={{ ...styles.title, marginBottom: '0.5rem', color: '#1a1a2e' }}>
              Finding your photos...
            </h2>
            <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: '2rem', lineHeight: 1.6 }}>
              Hi <strong>{data.name}</strong>, we're matching your selfie with photos from <strong>{event.name}</strong>.
              Your gallery will be ready in a moment.
            </p>
            <div style={{
              background: '#f0f4ff', border: '1.5px solid #a5b4fc',
              borderRadius: '1rem', padding: '1.25rem', marginBottom: '1.5rem'
            }}>
              <p style={{ margin: '0 0 0.5rem', color: '#4f46e5', fontWeight: 700, fontSize: '1rem' }}>
                Redirecting to your gallery in {countdown}s...
              </p>
              <div style={{ background: '#e0e7ff', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', background: '#4f46e5', borderRadius: 4,
                  width: `${((12 - countdown) / 12) * 100}%`,
                  transition: 'width 1s linear'
                }} />
              </div>
            </div>
            {!redirected && (
              <a href={data.gallery_url} style={{
                display: 'inline-block', padding: '0.75rem 2rem',
                background: 'linear-gradient(135deg, #1a1a2e, #0f3460)',
                color: '#fff', borderRadius: '0.75rem', fontWeight: 700,
                fontSize: '0.95rem', textDecoration: 'none'
              }}>
                Open Gallery Now →
              </a>
            )}
          </>
        ) : (
          <>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
            <h2 style={{ ...styles.title, marginBottom: '0.5rem' }}>You're Registered!</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>Welcome, {data.name}</p>
            <div style={styles.successBox}>
              <p style={{ margin: 0, color: '#166534', fontSize: '0.9rem', lineHeight: 1.6 }}>
                Your photos from <strong>{event.name}</strong> will be shared with you once
                the event is published by the organizer.
              </p>
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const ErrorScreen = ({ message }) => (
  <div style={styles.page}>
    <div style={styles.card}>
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <p style={{ color: '#dc2626' }}>{message}</p>
      </div>
    </div>
  </div>
);

const LoadingScreen = () => (
  <div style={styles.page}>
    <div style={styles.card}>
      <div style={{ textAlign: 'center', padding: '3rem 0', color: '#999' }}>
        Loading event details...
      </div>
    </div>
  </div>
);

// ── Styles ─────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    display: 'flex',
    alignItems: 'flex-start',   // align to top (or stretch if you want the card to grow)
    justifyContent: 'center',
    padding: 0,
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  card: {
    position: 'relative',            // to contain absolute background
    background: '#fff',
    borderRadius: '0rem',
    padding: '2rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
    overflow: 'hidden'               // keeps background inside rounded corners
  },
  // New background image div with fade‑from‑middle effect
  cardBackgroundImage: {
    position: 'absolute',
    top: '-21em',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'url(https://rarojewels.com/meta/register.jpeg)',
    backgroundSize: 'inherit',
    backgroundPosition: 'center',
    opacity: 0.45,                   // very subtle
    // Radial gradient mask: image is more visible in the center, fades to transparent at edges
    WebkitMaskImage: 'radial-gradient(circle, rgba(0,0,0,0.8) 30%, rgba(0,0,0,0) 80%)',
    maskImage: 'radial-gradient(circle, rgba(0,0,0,0.8) 30%, rgba(0,0,0,0) 80%)',
    pointerEvents: 'none',           // allow clicks to pass through to form elements
    zIndex: 0
  },
  // Wrapper for all content to sit above the background
  contentWrapper: {
    position: 'relative',
    zIndex: 1
  },
  header: { textAlign: 'left', marginBottom: '1rem' },
  logo: { fontSize: '2.5rem', marginBottom: '0.5rem' },
  title: { fontSize: '1.5rem', fontWeight: '700', color: '#1a1a2e', margin: '0 0 0.25rem' },
  eventName: { fontSize: '1.1rem', fontWeight: '600', color: '#1a1a2e', margin: '0.25rem 0 0' },
  eventMeta: { fontSize: '0.85rem', color: '#1a1a2e', margin: '0.25rem 0 0' },
  divider: { height: '1px', background: '#eee', margin: '0.25rem 0px' },
  instructions: { fontSize: '0.875rem', color: '#1a1a2e', marginBottom: '1.5rem', lineHeight: '1.5' },
  field: { marginBottom: '1.25rem' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#444', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { width: '100%', padding: '0.75rem 1rem', border: '1.5px solid #ddd', borderRadius: '0.75rem', fontSize: '1rem', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' },
  inputError: { borderColor: '#ef4444' },
  error: { color: '#ef4444', fontSize: '0.8rem', margin: '0.4rem 0 0' },
  cameraBtn: { width: '100%', padding: '0.75rem', background: '#f0f4ff', border: '2px dashed #a5b4fc', borderRadius: '0.75rem', color: '#4f46e5', fontWeight: '600', cursor: 'pointer', fontSize: '1rem' },
  cameraContainer: { position: 'relative', borderRadius: '0.75rem', overflow: 'hidden', background: '#000' },
  video: { width: '100%', display: 'block', transform: 'scaleX(-1)' },
  cameraLoading: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#aaa', fontSize: '0.85rem', margin: 0, pointerEvents: 'none' },
  captureBtn: { position: 'absolute', bottom: '1rem', left: '50%', transform: 'translateX(-50%)', background: '#fff', border: 'none', borderRadius: '50px', padding: '0.6rem 1.5rem', fontWeight: '700', fontSize: '1rem', cursor: 'pointer', color: '#dc2626', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', transition: 'opacity 0.2s' },
  captureBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  previewContainer: { position: 'relative', borderRadius: '0.75rem', overflow: 'hidden' },
  preview: { width: '100%', display: 'block', borderRadius: '0.75rem' },
  retakeBtn: { position: 'absolute', bottom: '0.75rem', right: '0.75rem', background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', borderRadius: '50px', padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '0.85rem' },
  registerBtn: { width: '100%', padding: '0.875rem', background: 'linear-gradient(135deg, #1a1a2e, #0f3460)', color: '#fff', border: 'none', borderRadius: '0.75rem', fontSize: '1.05rem', fontWeight: '700', cursor: 'pointer', marginTop: '0.5rem', transition: 'opacity 0.2s' },
  registerBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  submitError: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.75rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' },
  successBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1.5rem' },
  galleryLink: { color: '#0f3460', fontWeight: '600', textDecoration: 'none', fontSize: '0.95rem' },
  mobileChecking: { color: '#999', fontSize: '0.8rem', margin: '0.3rem 0 0' },
  alreadyBox: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1.25rem' },
  alreadyIcon: { fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 },
  alreadyTitle: { margin: '0 0 0.2rem', fontWeight: '700', fontSize: '0.95rem', color: '#166534' },
  alreadySubtitle: { margin: '0 0 0.6rem', fontSize: '0.82rem', color: '#166534' },
  galleryBtn: { display: 'inline-block', padding: '0.4rem 1rem', background: '#166534', color: '#fff', borderRadius: '0.5rem', fontSize: '0.85rem', fontWeight: '600', textDecoration: 'none' },
  returningBox: { background: '#f0f4ff', border: '1.5px solid #a5b4fc', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1.25rem' },
  returningTitle: { margin: '0 0 0.2rem', fontWeight: '700', fontSize: '0.95rem', color: '#1a1a2e' },
  returningSubtitle: { margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#555' },
  returningSelfiRow: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  returningThumb: { width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #a5b4fc', flexShrink: 0 },
  returningButtons: { display: 'flex', flexDirection: 'column', gap: '0.4rem', flex: 1 },
  reuseBtn: { padding: '0.45rem 0.75rem', borderRadius: '0.5rem', border: '1.5px solid #a5b4fc', background: '#fff', color: '#4f46e5', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', textAlign: 'left' },
  reuseBtnActive: { background: '#4f46e5', color: '#fff', borderColor: '#4f46e5' }
};

export default RegisterPage;
